import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Box } from "lucide-react";
import client, { displayName } from "@/lib/api";

export const SearchBar = ({ large = false, autoFocus = false }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const navigate = useNavigate();
  const ref = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setItems([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await client.get("/products/suggest", { params: { q } });
        setItems(data.items);
        setOpen(true);
      } catch { /* ignore */ }
    }, 160);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (sku) => { setOpen(false); setQ(""); navigate(`/product/${encodeURIComponent(sku)}`); };
  const search = () => { setOpen(false); navigate(`/catalogue?search=${encodeURIComponent(q)}`); };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    else if (e.key === "Enter") { if (active >= 0 && items[active]) go(items[active].sku); else search(); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative w-full" data-testid="search-bar">
      <div className={`flex items-center gap-2 bg-white border border-border rounded-md transition-all focus-within:border-orange focus-within:ring-2 focus-within:ring-orange/30 ${large ? "h-14 px-4" : "h-11 px-3"}`}>
        <Search className={`text-steel-light ${large ? "w-6 h-6" : "w-5 h-5"}`} strokeWidth={2.2} />
        <input
          ref={ref}
          data-testid="search-input"
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => { setQ(e.target.value); setActive(-1); }}
          onFocus={() => items.length && setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search by SKU, part number, name or brand…"
          className={`flex-1 bg-transparent outline-none placeholder:text-steel-light text-graphite ${large ? "text-lg" : "text-sm"}`}
        />
        {q && (
          <button data-testid="search-clear" onClick={() => { setQ(""); setItems([]); }} className="text-steel-light hover:text-graphite">
            <X className="w-4 h-4" />
          </button>
        )}
        <button data-testid="search-submit" onClick={search} className="hidden sm:flex items-center bg-orange hover:bg-orange-hover text-white font-medium rounded px-3 h-8 text-sm transition-colors">
          Search
        </button>
      </div>

      {open && items.length > 0 && (
        <div data-testid="search-suggestions" className="absolute z-50 mt-2 w-full bg-white border border-border rounded-md shadow-lg overflow-hidden gp-fade-up">
          {items.map((p, i) => (
            <button
              key={p.sku}
              data-testid={`suggest-${p.sku}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(p.sku)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-border last:border-0 ${active === i ? "bg-surface" : "bg-white"}`}
            >
              <div className="w-9 h-9 rounded bg-surface grid place-items-center overflow-hidden shrink-0">
                {p.main_image ? <img src={p.main_image} alt="" className="w-full h-full object-contain" /> : <Box className="w-4 h-4 text-steel-light" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-graphite truncate">{displayName(p)}</div>
                <div className="mono text-xs text-steel-light truncate">{p.sku}{p.brand ? ` · ${p.brand}` : ""}</div>
              </div>
              <span className={`w-2 h-2 rounded-full shrink-0 ${p.is_in_stock ? "bg-emerald-500" : "bg-steel-light"}`} />
            </button>
          ))}
          <button onClick={search} className="w-full text-center text-sm text-orange font-medium py-2.5 hover:bg-surface">
            See all results for “{q}”
          </button>
        </div>
      )}
    </div>
  );
};
